
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { TutorialSection, TutorialStep } from "@/types/tutorial";
import StepManager from "./StepManager";
import CollapsibleSection from "./CollapsibleSection";

interface SectionManagerProps {
  sections: TutorialSection[];
  onChange: (sections: TutorialSection[]) => void;
}

const SectionManager: React.FC<SectionManagerProps> = ({ sections, onChange }) => {
  const [allSectionsExpanded, setAllSectionsExpanded] = useState(true);

  const addSection = () => {
    const newSection: TutorialSection = {
      id: `section-${Date.now()}`,
      title: "",
      steps: [],
      collapsed: false,
      optional: false
    };
    onChange([...sections, newSection]);
    
    // Auto-focus the new section title after a brief delay
    setTimeout(() => {
      const newSectionElement = document.querySelector(`[data-section-id="${newSection.id}"] input`);
      if (newSectionElement) {
        (newSectionElement as HTMLElement).focus();
        newSectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const updateSection = (id: string, updates: Partial<TutorialSection>) => {
    onChange(sections.map(section => 
      section.id === id ? { ...section, ...updates } : section
    ));
  };

  const removeSection = (id: string) => {
    onChange(sections.filter(section => section.id !== id));
  };

  const updateSectionSteps = (sectionId: string, steps: TutorialStep[]) => {
    updateSection(sectionId, { steps });
  };

  const toggleAllSections = () => {
    setAllSectionsExpanded(!allSectionsExpanded);
    onChange(sections.map(section => ({ 
      ...section, 
      collapsed: allSectionsExpanded 
    })));
  };

  return (
    <CollapsibleSection 
      title="Tutorial Sections"
      defaultExpanded={true}
      headerActions={
        sections.length > 0 && (
          <Button variant="outline" size="sm" onClick={toggleAllSections}>
            {allSectionsExpanded ? <ChevronUp size={16} className="mr-2" /> : <ChevronDown size={16} className="mr-2" />}
            {allSectionsExpanded ? "Collapse All" : "Expand All"}
          </Button>
        )
      }
    >
      <div className="space-y-6 relative">
        {sections.map((section, index) => (
          <div 
            key={section.id} 
            className={`border-2 rounded-xl p-6 space-y-4 ${
              index % 2 === 0 
                ? 'bg-gradient-to-br from-blue-50/50 to-purple-50/30 border-blue-200/50 dark:from-blue-950/20 dark:to-purple-950/10 dark:border-blue-800/30' 
                : 'bg-gradient-to-br from-green-50/50 to-yellow-50/30 border-green-200/50 dark:from-green-950/20 dark:to-yellow-950/10 dark:border-green-800/30'
            }`}
            data-section-id={section.id}
          >
            <div className="flex items-center gap-2">
              <GripVertical size={16} className="text-muted-foreground cursor-grab" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="bg-background/80 px-3 py-1 rounded-full text-sm font-medium">
                    Section {index + 1} Title
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`optional-${section.id}`}
                      checked={section.optional || false}
                      onCheckedChange={(checked) => updateSection(section.id, { optional: !!checked })}
                    />
                    <Label
                      htmlFor={`optional-${section.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Optional
                    </Label>
                  </div>
                </div>
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(section.id, { title: e.target.value })}
                  placeholder="Enter section title"
                  className="bg-background/80 font-medium"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeSection(section.id)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            
            <div className="bg-background/60 rounded-lg p-4">
              <StepManager
                steps={section.steps}
                onChange={(steps) => updateSectionSteps(section.id, steps)}
              />
            </div>
          </div>
        ))}
        
        <div className="sticky bottom-4 z-20 flex justify-center pt-4">
          <Button 
            variant="default" 
            onClick={addSection}
            className="bg-primary shadow-lg border-2 border-primary/20"
          >
            <Plus size={16} className="mr-2" />
            Add Section
          </Button>
        </div>
        
        {sections.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-lg mb-2">No sections yet</p>
            <p className="text-sm">Click "Add Section" to get started with your tutorial.</p>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default SectionManager;
